/*
 * Copyright 2025 iodyne.  All rights reserved.
 * Use is subject to license terms.
 */

import { AuthStates, getAuthState } from "@/lib/auth/auth_state";
import { type ModelError } from "@/lib/models/errors";
import type { CloudUserDetails, CloudUserDetailsUpdate, CloudUserType, NewCloudUserDetails, UserConsents } from "@/lib/models/people";
import { createEntityAdapter, createSelector, type EntityState } from "@reduxjs/toolkit";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { translate as t } from "@/i18n/client_nohook";
import { responseForAPIError } from "@/lib/datastore/slices/helpers";
import { teamsSlice } from "./teams";
import { projectsSlice } from "./projects";

// User Storage
enum UserAPIErrors {
  userDNEError = "UserDNEError",
  complexPasswordRequiredError = "ComplexPasswordRequiredError",
  missingRequiredConsentsError = "MissingRequiredConsentsError",
  userAccessNotAllowedError = "UserAccessNotAllowedError",
  noAccountPrivilegesError = "NoAccountPrivilegesError",
  deleteLastOrgAdminForbiddenError = "DeleteLastOrgAdminForbiddenError",
  teamsReferenceUserError = "TeamsReferenceUserError",
  userAlreadyExistsError = "UserAlreadyExistsError",
  invalidUserActivationTokenError = "InvalidUserActivationTokenError",
  invalidOAuthClientIDError = "InvalidOAuthClientIDError",
  userDisabledError = "UserDisabledError",
  userAlreadyActivatedError = "UserAlreadyActivatedError",
  projectMembershipError = "ProjectMembershipError"
}

const usersAdapter = createEntityAdapter({
  selectId: (e:CloudUserType) => e.email
});
const usersInitialState = usersAdapter.getInitialState();
export const usersSlice = createApi({
  reducerPath: "users_api",
  tagTypes: ["User", "UserActivationToken"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.IOD_CLOUD_PORTAL_ACCOUNT_API_URI}/v2/ica/`,
    prepareHeaders: (headers, { getState }) => {
      headers.set("Authorization", `Bearer ${getAuthState(AuthStates.userAccessToken)}`)
      return headers;
    }
  }),
  endpoints: build => ({
    // List Users
    listUsers: build.query<EntityState<CloudUserType, string>, { org_id: string }>({
      //@ts-ignore
      providesTags: (result,_,arg) => result?.ids.map((id) => ({type: "User", email:id})),
      query: ({ org_id }) => `org/${org_id}/users`,
      transformResponse(resp: CloudUserType[], _meta, { org_id }) {
        return usersAdapter.setMany(usersInitialState, resp);
      },
    }),

    // Retrieve User Detail
    getUser: build.query<EntityState<CloudUserType, string>, { email: string }>({
      //@ts-ignore
      providesTags: (result,_,email) => [{type: "User", email:email}],
      query: ({ email }) => `user/${email}`,
      transformResponse(resp: CloudUserType, _meta, { email }) {
        return usersAdapter.setOne(usersInitialState, resp);
      },
      async transformErrorResponse(resp: { status: number, data: Record<string, any> }): Promise<ModelError> {
        return responseForAPIError(
          resp,
          {
            [UserAPIErrors.userDNEError]: await t("user_mgmt.api_errors.user_dne")
          }
        );
      }
    }),

    // Create User
    createUser: build.mutation<EntityState<CloudUserType, string>, { org_id: string, email: string, details: NewCloudUserDetails }>({
      invalidatesTags: (result, error, email) => [
        { type: "User", email: email }
      ],
      query: ({ org_id, email, details }) => ({
        url: `org/${org_id}/user/${email}`,
        method: "POST",
        body: details
      }),
      onQueryStarted: ({}, api) => {
        api.queryFulfilled.then(() => {
          api.dispatch(projectsSlice.util.invalidateTags([{ type: "ProjectACLs" }]))
        });
      },
      transformResponse(resp: CloudUserType, meta, { details }) {
        return usersAdapter.setOne(usersInitialState, resp);
      },
      async transformErrorResponse(resp: { status: number, data: Record<string, any> }): Promise<ModelError> {
        return responseForAPIError(
          resp,
          {
            [UserAPIErrors.userDNEError]: await t("user_mgmt.api_errors.user_dne"),
            [UserAPIErrors.complexPasswordRequiredError]: await t("user_mgmt.api_errors.complex_password_required"),
            [UserAPIErrors.missingRequiredConsentsError]: await t("user_mgmt.api_errors.complex_password_required"),
            [UserAPIErrors.noAccountPrivilegesError]: await t("user_mgmt.api_errors.no_account_privileges"),
            [UserAPIErrors.userAccessNotAllowedError]: await t("user_mgmt.api_errors.user_access_not_allowed"),
            [UserAPIErrors.userAlreadyExistsError]: await t("user_mgmt.api_errors.user_email_already_registered")
          }
        );
      }
    }),

    // Activate User
    activateUser: build.mutation<boolean, {
      org_id: string,
      activation_token: string,
      client_id: string,
      password?: string,
      consents?: UserConsents
    }>({
      invalidatesTags: (result, error, email) => [
        { type: "User"}
      ],
      query: ({ org_id, activation_token, client_id, password, consents }) => ({
        url: `org/${org_id}/user/activate/${activation_token}`,
        method: "PUT",
        body: {
          client_id: client_id,
          ...(password !== undefined && { password }),
          ...(consents !== undefined && { consents })
        }
      }),
      async transformErrorResponse(resp: { status: number, data: Record<string, any> }): Promise<ModelError> {
        return responseForAPIError(
          resp,
          {
            [UserAPIErrors.invalidUserActivationTokenError]: await t("user_mgmt.api_errors.invalid_activation_token"),
            [UserAPIErrors.invalidOAuthClientIDError]: await t("user_mgmt.api_errors.invalid_oauth_client_id"),
            [UserAPIErrors.userDisabledError]: await t("user_mgmt.api_errors.user_disabled"),
            [UserAPIErrors.userAlreadyActivatedError]: await t("user_mgmt.api_errors.user_already_activated"),
            [UserAPIErrors.complexPasswordRequiredError]: await t("user_mgmt.api_errors.complex_password_required"),
            [UserAPIErrors.missingRequiredConsentsError]: await t("user_mgmt.api_errors.missing_required_consents")
          }
        );
      }
    }),

    // Refresh Activation Email
    refreshActivationEmail: build.mutation<{activation_token:string, activation_uri:string}, {
      email: string,
      initial_project_id?: string
    }>({
      query: ({ email, initial_project_id }) => ({
        url: `user/${email}/activation/refresh`,
        method: "PUT",
        body: {
          email: email,
          initial_project_id: initial_project_id ? initial_project_id : "",
          resend_activation_email: true
        }
      }),
      async transformErrorResponse(resp: { status: number, data: Record<string, any> }): Promise<ModelError> {
        return responseForAPIError(
          resp,
          {
            [UserAPIErrors.userAlreadyActivatedError]: await t("user_mgmt.api_errors.user_already_activated"),
            [UserAPIErrors.userDNEError]: await t("user_mgmt.api_errors.user_dne"),
            [UserAPIErrors.projectMembershipError]: await t("user_mgmt.api_errors.project_membership_error")
          }
        );
      }
    }),

    // Refresh Activation Token
    refreshActivationToken: build.query<{activation_token:string, activation_uri:string}, {
      email: string,
      initial_project_id?: string
    }>({
      query: ({ email, initial_project_id }) => ({
        url: `user/${email}/activation/refresh`,
        method: "PUT",
        body: {
          email: email,
          initial_project_id: initial_project_id ? initial_project_id : "",
          resend_activation_email: false
        }
      }),
      async transformErrorResponse(resp: { status: number, data: Record<string, any> }): Promise<ModelError> {
        return responseForAPIError(
          resp,
          {
            [UserAPIErrors.userAlreadyActivatedError]: await t("user_mgmt.api_errors.user_already_activated"),
            [UserAPIErrors.userDNEError]: await t("user_mgmt.api_errors.user_dne"),
            [UserAPIErrors.projectMembershipError]: await t("user_mgmt.api_errors.project_membership_error")
          }
        );
      }
    }),

    // Delete User
    deleteUser: build.mutation<EntityState<CloudUserType, string>, { email: string, force?:boolean }>({
      invalidatesTags: (result, error, email) => [
        { type: "User", email: email }
      ],
      query: ({ email, force }) => ({
        url: `user/${email}`,
        method: "DELETE",
        params: force ? {"force" : true} : {}
      }),
      onQueryStarted: ({ email }, api) => {
        //Invalidate team cache entries referenced by this user
        api.queryFulfilled.then(() => {
          const queries = api.getState().users_api.queries;
          Object.keys(queries).forEach((key) => {
            if(queries[key] && queries[key].endpointName == "getUser") {
              const matches = (queries[key].data as any)?.entities;
              Object.keys(matches).forEach((user_key:string) => {
                if(user_key == email) {
                  Object.keys(matches[user_key].teams).forEach((team_id) => {
                    //@ts-ignore
                    api.dispatch(teamsSlice.util.invalidateTags([{ type: "TeamMembership", team_id: team_id}]));
                    //@ts-ignore
                    api.dispatch(teamsSlice.util.invalidateTags([{ type: "Team", team_id: team_id}]));
                  })
                }
              })
            }
          });
        });
      },
      async transformErrorResponse(resp: { status: number, data: Record<string, any> }): Promise<ModelError> {
        return responseForAPIError(
          resp,
          {
            [UserAPIErrors.userDNEError]: await t("user_mgmt.api_errors.user_dne"),
            [UserAPIErrors.userAccessNotAllowedError]: await t("user_mgmt.api_errors.user_access_not_allowed"),
            [UserAPIErrors.deleteLastOrgAdminForbiddenError]: await t("user_mgmt.api_errors.delete_last_admin"),
            [UserAPIErrors.teamsReferenceUserError]: await t("user_mgmt.api_errors.delete_user_has_teams_memberships")
          }
        );
      }
    }),

    // Update User Detail
    updateUser: build.mutation<EntityState<CloudUserType, string>, { email: string, details: Partial<CloudUserDetailsUpdate>, updating_self:boolean }>({
      invalidatesTags: (result, error, email) => [
        { type: "User", email: email }
      ],
      query: ({ email, details, updating_self }) => {
        // When modifying the user's own record, remove the enabled 
        // and is_admin flags since users are forbidden to change
        // those attributes on themselves.
        if (updating_self) {
          details.enabled = undefined;
          details.is_admin = undefined;
        }

        return ({
          url: `user/${email}`,
          method: "PUT",
          body: details
        })
      },
      transformResponse(resp: CloudUserType, meta, { email, details }) {
        resp.email = email;
        return usersAdapter.setOne(usersInitialState, resp);
      },
      async transformErrorResponse(resp: { status: number, data: Record<string, any> }): Promise<ModelError> {
        return responseForAPIError(
          resp,
          {
            [UserAPIErrors.userDNEError]: await t("user_mgmt.api_errors.user_dne"),
            [UserAPIErrors.complexPasswordRequiredError]: await t("user_mgmt.api_errors.complex_password_required"),
            [UserAPIErrors.missingRequiredConsentsError]: await t("user_mgmt.api_errors.missing_required_consents"),
            [UserAPIErrors.noAccountPrivilegesError]: await t("user_mgmt.api_errors.no_account_privileges"),
            [UserAPIErrors.userAccessNotAllowedError]: await t("user_mgmt.api_errors.user_access_not_allowed")
          }
        );
      }
    }),

    // Update User Profile Art
    setProfileArt: build.mutation<EntityState<CloudUserDetails, string>, { email:string, image: File | undefined }>({
      invalidatesTags: (result, error, email) => [
        { type: "User", email: email }
      ],
      query: ({ email, image }) => {
        let body_form_data = new FormData();
        image && body_form_data.append("image", image);
        return {
          url: `user/${email}/art`,
          method: "POST",
          body: body_form_data,
          formData: true
        }
      }
    })
  })
});

export const {
  useGetUserQuery,
  useUpdateUserMutation,
  useCreateUserMutation,
  useListUsersQuery,
  useSetProfileArtMutation,
  useDeleteUserMutation,
  useActivateUserMutation,
  useRefreshActivationEmailMutation,
  useRefreshActivationTokenQuery
} = usersSlice;

export const usersSelectors = usersAdapter.getSelectors();

export const selectUserByUID = (user_id:string) => createSelector(
  usersSelectors.selectEntities,
  (users:{[key:string]: CloudUserType}) => {

    for(const email of Object.keys(users)) {
      
      if(users[email] && users[email].user_id === user_id) {
        return users[email];
      }
    }
 
    return undefined;
  }
);

export const selectUsersMap = createSelector(
  usersSelectors.selectEntities,
  (users:{[key:string]: CloudUserType}) => {
    var users_map:{[key:string]: CloudUserType} = {};

    Object.keys(users).forEach((email) => {
      users_map[users[email].user_id] = users[email];
    });
 
    return users_map;
  }
);